import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

function getArticleJsonLd(article: typeof articles[string]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': article.h1,
    'description': article.metaDescription,
    'author': {
      '@type': 'Organization',
      'name': 'KakobuyFinds.ink',
      'url': 'https://kakobuyfinds.ink',
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'KakobuyFinds.ink',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
      },
    },
    'datePublished': '2026-07-08',
    'dateModified': '2026-07-08',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://kakobuyfinds.ink/artykul/${article.slug}`,
    },
    'inLanguage': 'pl-PL',
    'isAccessibleForFree': true,
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = slug ? articles[slug] : undefined;
  const displayCategories = categories.slice(0, 8);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Artykuł nie znaleziony</h1>
          <p className="text-foreground-500 mb-6">Przepraszamy, szukany artykuł nie istnieje.</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Powrót do strony głównej
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  const jsonLd = getArticleJsonLd(article);

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />
      <article className="pt-24 pb-8">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />

        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li>
                <a href="/" className="hover:text-primary-500 transition-colors duration-150 cursor-pointer">
                  Strona główna
                </a>
              </li>
              <li>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              </li>
              <li className="text-foreground-700 font-medium">
                {article.h1.length > 60 ? article.h1.slice(0, 60) + '...' : article.h1}
              </li>
            </ol>
          </nav>

          <header className="mb-10">
            <span className="inline-block text-xs font-medium text-primary-500 uppercase tracking-wider mb-4 border border-primary-200/60 rounded-full px-3 py-1">
              {article.badge}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.h1}
            </h1>
            <p className="text-sm text-foreground-500">
              {article.meta}
            </p>
          </header>

          <div className="prose-custom space-y-8 text-foreground-700 leading-relaxed">
            <p className="text-lg text-foreground-800 leading-relaxed">
              {article.intro}
            </p>

            {article.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  {section.title}
                </h2>
                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className={pIdx < section.paragraphs.length - 1 ? 'mb-4' : ''}>
                    {p}
                  </p>
                ))}
              </section>
            ))}

            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                {article.conclusion.title}
              </h2>
              {article.conclusion.paragraphs.map((p, pIdx) => (
                <p key={pIdx} className={pIdx < article.conclusion.paragraphs.length - 1 ? 'mb-4' : ''}>
                  {p}
                </p>
              ))}
            </section>
          </div>

          <div className="mt-12 p-8 bg-background-100 rounded-2xl text-center">
            <p className="text-foreground-700 mb-4">{article.cta.text}</p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              {article.cta.button}
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>

          <div className="mt-12 border-t border-background-200/70 pt-8">
            <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              {article.relatedTitle}
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {displayCategories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-4 py-3 rounded-lg border border-background-200/60 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all duration-150 cursor-pointer bg-background-50"
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-primary-500`}></i>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/artykul/porownanie-spreadsheet-zakupowych')}
                className="text-xs px-4 py-2 rounded-full border border-secondary-200/60 text-secondary-700 hover:bg-secondary-100 hover:border-secondary-300 transition-all duration-150 cursor-pointer whitespace-nowrap"
              >
                Porównanie Spreadsheetów
              </button>
              <button
                onClick={() => navigate('/artykul/bezpieczne-korzystanie-ze-spreadsheet')}
                className="text-xs px-4 py-2 rounded-full border border-accent-200/60 text-accent-700 hover:bg-accent-100 hover:border-accent-300 transition-all duration-150 cursor-pointer whitespace-nowrap"
              >
                Poradnik Bezpieczeństwa
              </button>
              <button
                onClick={() => navigate('/artykul/kakobuy-spreadsheet-sneakersy-buty')}
                className="text-xs px-4 py-2 rounded-full border border-background-200/60 text-foreground-600 hover:bg-background-100 hover:border-background-300 transition-all duration-150 cursor-pointer whitespace-nowrap"
              >
                Sneakersy i Buty
              </button>
              <button
                onClick={() => navigate('/artykul/kakobuy-spreadsheet-moda-odziez')}
                className="text-xs px-4 py-2 rounded-full border border-background-200/60 text-foreground-600 hover:bg-background-100 hover:border-background-300 transition-all duration-150 cursor-pointer whitespace-nowrap"
              >
                Moda i Odzież
              </button>
              <button
                onClick={() => navigate('/artykul/kakobuy-sheet-kompletny-przewodnik')}
                className="text-xs px-4 py-2 rounded-full border border-primary-200/60 text-primary-600 hover:bg-primary-100 hover:border-primary-300 transition-all duration-150 cursor-pointer whitespace-nowrap"
              >
                Pełny Przewodnik
              </button>
            </div>
          </div>

          <div className="mt-10 text-center">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-150 cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Powrót do strony głównej
            </a>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}